import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "@/utils/axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Shield, Users, AlertCircle, MessageSquare, LogOut, User, DollarSign, 
  CheckCircle, Clock, AlertTriangle, FileText, BarChart3, Briefcase, Home,
  UserPlus, Calendar, ArrowRight, TrendingUp, CreditCard, File, Download, FileSpreadsheet, Menu, X, Languages, Trash2, RotateCcw, Megaphone, Pin, Eye, EyeOff, Video, Image, Edit, Plus, Key
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";


// Dashboard Home View
function DashboardHomeView({ stats }) {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Admin Portal</h1>
        <p className="text-slate-600 text-lg">Quick overview of your system</p>
      </div>

      {/* Quick Access Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Workers Requiring Renewal */}
        <Card className="border-none shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-blue-600" />
                <CardTitle className="text-xl">Workers Requiring Renewal</CardTitle>
              </div>
              <Link to="/admin/visa-tracker" className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-slate-500">
              <p>No data available</p>
            </div>
          </CardContent>
        </Card>

        {/* Recent Complaints */}
        <Card className="border-none shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <CardTitle className="text-xl">Recent Complaints</CardTitle>
              </div>
              <Link to="/admin/complaints" className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-slate-500">
              <p>No data available</p>
            </div>
          </CardContent>
        </Card>

        {/* Overdue Payments */}
        <Card className="border-none shadow-sm md:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-red-600" />
                <CardTitle className="text-xl">Overdue Payments</CardTitle>
              </div>
              <Link to="/admin/payments" className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-slate-500">
              <p>No data available</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Staff Management View
function StaffManagementView({ stats }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isStaffDialogOpen, setIsStaffDialogOpen] = useState(false);
  const [staffFormData, setStaffFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    phone: "",
  });

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      const response = await axios.get('/staff');
      setStaff(response.data);
    } catch (error) {
      console.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/auth/register', { ...staffFormData, role: 'staff' });
      toast.success("Staff member added successfully");
      setIsStaffDialogOpen(false);
      setStaffFormData({ email: "", password: "", full_name: "", phone: "" });
      await fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add staff member");
    }
  };

  const handleDeleteStaff = async (staffId, staffName) => {
    if (!window.confirm(`Are you sure you want to delete ${staffName}? They can be restored from the Deleted Users tab.`)) {
      return;
    }
    
    try {
      await axios.delete(`/users/${staffId}`);
      toast.success(`${staffName} has been deleted`);
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete staff");
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Staff Management</h1>
        <p className="text-slate-600 text-lg">Add new staff members and manage existing personnel.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-none shadow-sm" data-testid="total-staff-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <p className="text-slate-600 text-sm font-medium">Total Staff</p>
                <p className="text-4xl font-bold text-slate-900">{stats.totalStaff || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm" data-testid="total-workers-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center">
                <Briefcase className="w-8 h-8 text-emerald-600" />
              </div>
              <div>
                <p className="text-slate-600 text-sm font-medium">Total Assigned Workers</p>
                <p className="text-4xl font-bold text-slate-900">{stats.totalWorkers || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Staff Button */}
      <div>
        <Dialog open={isStaffDialogOpen} onOpenChange={setIsStaffDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-staff-button">
              <UserPlus className="w-4 h-4 mr-2" />
              Add New Staff
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Staff Member</DialogTitle>
              <DialogDescription>
                Enter the details below to create a new staff record
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleStaffSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="staff_name">Full Name</Label>
                <Input
                  id="staff_name"
                  value={staffFormData.full_name}
                  onChange={(e) => setStaffFormData({ ...staffFormData, full_name: e.target.value })}
                  required
                  data-testid="staff-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="staff_email">Email</Label>
                <Input
                  id="staff_email"
                  type="email"
                  value={staffFormData.email}
                  onChange={(e) => setStaffFormData({ ...staffFormData, email: e.target.value })}
                  required
                  data-testid="staff-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="staff_phone">Phone</Label>
                <Input
                  id="staff_phone"
                  value={staffFormData.phone}
                  onChange={(e) => setStaffFormData({ ...staffFormData, phone: e.target.value })}
                  data-testid="staff-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="staff_password">Password</Label>
                <Input
                  id="staff_password"
                  type="password"
                  value={staffFormData.password}
                  onChange={(e) => setStaffFormData({ ...staffFormData, password: e.target.value })}
                  required
                  data-testid="staff-password-input"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="submit-staff-button">
                Create Staff Member
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Staff List */}
      <Card className="border-none shadow-sm">
        <CardHeader>
          <CardTitle>Staff Members</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p>Loading staff...</p>
          ) : staff.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No staff members yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {staff.map((member) => (
                <div key={member.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="bg-blue-100 text-blue-600 font-semibold">
                        {member.full_name.split(" ").map(n => n[0]).join("").substring(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold">{member.full_name}</p>
                      <p className="text-sm text-slate-600">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">Staff</Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteStaff(member.id, member.full_name)}
                      data-testid={`delete-staff-${member.id}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Workload Overview View
function WorkloadOverviewView() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWorkload();
  }, []);

  const fetchWorkload = async () => {
    try {
      const response = await axios.get('/staff');
      setStaff(response.data);
    } catch (error) {
      console.error('Failed to load workload data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Workload Overview</h1>
        <p className="text-slate-600 text-lg">Monitor how workers are distributed among your staff.</p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Workload Table */}
        <Card className="col-span-8 border-none shadow-sm">
          <CardHeader>
            <CardTitle>Workload Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-slate-600 font-medium">Staff Name</th>
                    <th className="text-left py-3 px-4 text-slate-600 font-medium">Staff ID</th>
                    <th className="text-left py-3 px-4 text-slate-600 font-medium">Role</th>
                    <th className="text-left py-3 px-4 text-slate-600 font-medium">Assigned Workers</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="text-center py-12 text-slate-500">
                        Loading...
                      </td>
                    </tr>
                  ) : staff.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center py-12 text-slate-500">
                        No staff data available.
                      </td>
                    </tr>
                  ) : (
                    staff.map((member) => (
                      <tr key={member.id} className="border-b last:border-0">
                        <td className="py-4 px-4">{member.full_name}</td>
                        <td className="py-4 px-4 text-slate-600">{member.id.substring(0, 8)}</td>
                        <td className="py-4 px-4"><Badge variant="secondary">Staff</Badge></td>
                        <td className="py-4 px-4">0</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Distribution Chart */}
        <Card className="col-span-4 border-none shadow-sm">
          <CardHeader>
            <CardTitle>Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center text-slate-500">
              <p>No distribution data</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Members View (Worker Membership)
function MembersView({ user }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const navigate = useNavigate();
  
  const isSuperAdmin = user?.role === "super_admin";

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await axios.get('/members');
      setMembers(response.data);
    } catch (error) {
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMember = async (e, memberId, memberName) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!window.confirm(`Are you sure you want to delete ${memberName}? They can be restored from the Deleted Users tab.`)) {
      return;
    }
    
    try {
      await axios.delete(`/users/${memberId}`);
      toast.success(`${memberName} has been deleted`);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete member");
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`/admin/users/${selectedMember.id}/set-password`, { password: newPassword });
      toast.success(`Password updated for ${selectedMember.full_name}`);
      setShowPasswordModal(false);
      setNewPassword("");
      setSelectedMember(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const openPasswordModal = (e, member) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedMember(member);
    setNewPassword("");
    setShowPasswordModal(true);
  };

  if (loading) {
    return <div className="p-6">Loading members...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Worker Membership</h1>
        <p className="text-slate-600 text-lg">View and manage all registered workers.</p>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">{members.length} total members</p>
        {isSuperAdmin && (
          <Badge className="bg-purple-100 text-purple-700">
            <Shield className="w-3 h-3 mr-1" />
            Super Admin: Can change passwords
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {members.map((member) => (
          <Card key={member.id} className="card-hover h-full border-none shadow-sm" data-testid={`member-card-${member.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <Link to={`/admin/members/${member.id}`} className="flex items-center gap-3 flex-1">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-slate-900 text-white font-semibold">
                      {member.full_name.split(" ").map(n => n[0]).join("").substring(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <CardTitle className="text-lg">{member.full_name}</CardTitle>
                    <CardDescription className="text-sm">{member.email}</CardDescription>
                  </div>
                </Link>
                <div className="flex items-center gap-1">
                  {isSuperAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                      onClick={(e) => openPasswordModal(e, member)}
                      title="Change Password"
                      data-testid={`change-password-${member.id}`}
                    >
                      <Key className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={(e) => handleDeleteMember(e, member.id, member.full_name)}
                    data-testid={`delete-member-${member.id}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Link to={`/admin/members/${member.id}`}>
                <div className="space-y-2 text-sm">
                  {member.employer && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Employer:</span>
                      <span className="font-medium">{member.employer}</span>
                    </div>
                  )}
                  {member.nationality && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Nationality:</span>
                      <span className="font-medium">{member.nationality}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Status:</span>
                    <Badge variant={member.subscription_status === "active" ? "default" : "destructive"} className="text-xs">
                      {member.subscription_status || "pending"}
                    </Badge>
                  </div>
                </div>
              </Link>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Change Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-600" />
              Change Member Password
            </DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600">Changing password for:</p>
                <p className="font-semibold text-slate-900">{selectedMember.full_name}</p>
                <p className="text-sm text-slate-500">{selectedMember.email}</p>
              </div>
              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="Enter new password (min 6 characters)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowPasswordModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleChangePassword}
                  disabled={changingPassword || newPassword.length < 6}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {changingPassword ? "Changing..." : "Change Password"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Member Detail View
function MemberDetailView({ memberId }) {
  const [member, setMember] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [editForm, setEditForm] = useState({
    full_name: "",
    phone: "",
    passport_number: "",
    nationality: "",
    employer: "",
    visa_expiry: "",
    subscription_status: ""
  });

  useEffect(() => {
    fetchMemberDetail();
    fetchStaff();
    fetchAssignment();
  }, [memberId]);

  const fetchMemberDetail = async () => {
    try {
      const response = await axios.get(`/members/${memberId}`);
      setMember(response.data);
      // Pre-fill edit form
      setEditForm({
        full_name: response.data.full_name || "",
        phone: response.data.phone || "",
        passport_number: response.data.passport_number || "",
        nationality: response.data.nationality || "",
        employer: response.data.employer || "",
        visa_expiry: response.data.visa_expiry || "",
        subscription_status: response.data.subscription_status || "pending"
      });
    } catch (error) {
      toast.error("Failed to load member details");
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await axios.get('/staff');
      setStaff(response.data);
    } catch (error) {
      console.error("Failed to load staff");
    }
  };

  const fetchAssignment = async () => {
    try {
      const response = await axios.get(`/members/${memberId}/assignment`);
      setAssignment(response.data);
    } catch (error) {
      console.error("No assignment found");
    }
  };

  const handleAssignStaff = async () => {
    if (!selectedStaffId) {
      toast.error("Please select a staff member");
      return;
    }

    try {
      await axios.post(`/members/${memberId}/assign`, {
        staff_id: selectedStaffId
      });
      toast.success("Staff assigned successfully");
      setIsAssignDialogOpen(false);
      fetchAssignment();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign staff");
    }
  };

  const handleCloseCase = async () => {
    if (!assignment) return;

    try {
      await axios.patch(`/assignments/${assignment.id}/close`);
      toast.success("Case closed successfully");
      fetchAssignment();
    } catch (error) {
      toast.error("Failed to close case");
    }
  };

  const handleRemoveAssignment = async () => {
    if (!assignment) return;

    try {
      await axios.delete(`/assignments/${assignment.id}`);
      toast.success("Assignment removed");
      setAssignment(null);
    } catch (error) {
      toast.error("Failed to remove assignment");
    }
  };

  const handleUpdateMember = async () => {
    try {
      await axios.patch(`/members/${memberId}`, editForm);
      toast.success("Member details updated successfully");
      setIsEditDialogOpen(false);
      fetchMemberDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update member");
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  if (!member) {
    return <div className="p-6">Member not found</div>;
  }

  return (
    <div className="space-y-6" data-testid="member-detail-view">
      <div className="flex items-center gap-4">
        <Link to="/admin/members">
          <Button variant="outline">← Back to Members</Button>
        </Link>
      </div>

      {/* Assignment Status Card */}
      <Card className="border-none shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">Case Assignment</CardTitle>
              <CardDescription>Manage staff assignment for this worker</CardDescription>
            </div>
            {!assignment || assignment.case_status === "closed" ? (
              <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="assign-staff-button">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Assign Staff
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Assign Staff to Member</DialogTitle>
                    <DialogDescription>
                      Select a staff member to handle this worker's case
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Select Staff Member</Label>
                      <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
                        <SelectTrigger data-testid="staff-select">
                          <SelectValue placeholder="Choose a staff member" />
                        </SelectTrigger>
                        <SelectContent>
                          {staff.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.full_name} ({s.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleAssignStaff} className="w-full" data-testid="confirm-assign-button">
                      Assign Staff
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          {assignment && assignment.case_status === "open" ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg">{assignment.staff_name}</p>
                    <p className="text-sm text-slate-600">Assigned on {new Date(assignment.assigned_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <Badge variant="default" className="text-sm">Open Case</Badge>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCloseCase} variant="outline" data-testid="close-case-button">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Close Case
                </Button>
                <Button onClick={handleRemoveAssignment} variant="outline" className="text-red-600" data-testid="remove-assignment-button">
                  Remove Assignment
                </Button>
              </div>
            </div>
          ) : assignment && assignment.case_status === "closed" ? (
            <div className="p-4 bg-emerald-50 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <p className="font-semibold text-emerald-900">Case Closed</p>
              </div>
              <p className="text-sm text-slate-600">
                Handled by <span className="font-medium">{assignment.staff_name}</span>
              </p>
              <p className="text-sm text-slate-600">
                Closed on {new Date(assignment.closed_at).toLocaleDateString()}
              </p>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No staff assigned to this worker</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Member Details Card */}
      <Card className="border-none shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                <AvatarFallback className="bg-slate-900 text-white text-xl font-semibold">
                  {member.full_name.split(" ").map(n => n[0]).join("").substring(0, 2)}
                </AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-2xl">{member.full_name}</CardTitle>
                <CardDescription className="text-base">{member.email}</CardDescription>
              </div>
            </div>
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="edit-member-button">
                  <FileText className="w-4 h-4 mr-2" />
                  Edit Details
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Member Details</DialogTitle>
                  <DialogDescription>Update worker information</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Full Name</label>
                    <Input
                      value={editForm.full_name}
                      onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                      data-testid="edit-full-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Phone</label>
                    <Input
                      value={editForm.phone}
                      onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                      data-testid="edit-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Passport Number</label>
                    <Input
                      value={editForm.passport_number}
                      onChange={(e) => setEditForm({...editForm, passport_number: e.target.value})}
                      data-testid="edit-passport"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Nationality</label>
                    <Input
                      value={editForm.nationality}
                      onChange={(e) => setEditForm({...editForm, nationality: e.target.value})}
                      data-testid="edit-nationality"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Employer</label>
                    <Input
                      value={editForm.employer}
                      onChange={(e) => setEditForm({...editForm, employer: e.target.value})}
                      data-testid="edit-employer"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Visa Expiry Date</label>
                    <Input
                      type="date"
                      value={editForm.visa_expiry}
                      onChange={(e) => setEditForm({...editForm, visa_expiry: e.target.value})}
                      data-testid="edit-visa-expiry"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Subscription Status</label>
                    <select
                      className="w-full p-2 border rounded-md"
                      value={editForm.subscription_status}
                      onChange={(e) => setEditForm({...editForm, subscription_status: e.target.value})}
                      data-testid="edit-subscription-status"
                    >
                      <option value="pending">Pending</option>
                      <option value="pending_review">Pending Review</option>
                      <option value="active">Active</option>
                      <option value="expired">Expired</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="payment_rejected">Payment Rejected</option>
                    </select>
                  </div>
                </div>
                <DialogFooter className="sticky bottom-0 bg-white pt-4 border-t">
                  <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleUpdateMember} data-testid="save-member-button">Save Changes</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-slate-900">Personal Information</h3>
              <div className="space-y-3">
                <InfoRow label="Phone" value={member.phone || "N/A"} />
                <InfoRow label="Passport Number" value={member.passport_number || "N/A"} />
                <InfoRow label="Nationality" value={member.nationality || "N/A"} />
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-slate-900">Employment Details</h3>
              <div className="space-y-3">
                <InfoRow label="Employer" value={member.employer || "N/A"} />
                <InfoRow label="Visa Expiry" value={member.visa_expiry || "N/A"} />
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium text-slate-600">Subscription Status:</span>
                  <Badge variant={member.subscription_status === "active" ? "default" : "destructive"}>
                    {member.subscription_status}
                  </Badge>
                </div>
                <InfoRow label="Monthly Fee" value={`RM ${member.monthly_fee || 0}`} />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100">
      <span className="text-sm font-medium text-slate-600">{label}:</span>
      <span className="text-sm text-slate-900">{value}</span>
    </div>
  );
}

// Complaints View
function ComplaintsView() {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState({});
  const [translations, setTranslations] = useState({});

  useEffect(() => {
    fetchComplaints();
  }, []);

  const fetchComplaints = async () => {
    try {
      const response = await axios.get('/complaints');
      setComplaints(response.data);
    } catch (error) {
      toast.error("Failed to load complaints");
    } finally {
      setLoading(false);
    }
  };

  const updateComplaintStatus = async (complaintId, newStatus) => {
    try {
      await axios.patch(`/complaints/${complaintId}`, { status: newStatus });
      toast.success("Complaint status updated");
      fetchComplaints();
    } catch (error) {
      toast.error("Failed to update complaint");
    }
  };

  const translateComplaint = async (complaintId, text) => {
    setTranslating(prev => ({ ...prev, [complaintId]: true }));
    try {
      const response = await axios.post('/complaints/translate', {
        text: text,
        target_language: "English"
      });
      setTranslations(prev => ({
        ...prev,
        [complaintId]: response.data.translated_text
      }));
      toast.success("Translation complete");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Translation failed");
    } finally {
      setTranslating(prev => ({ ...prev, [complaintId]: false }));
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending": return <Clock className="w-4 h-4" />;
      case "in_progress": return <AlertTriangle className="w-4 h-4" />;
      case "resolved": return <CheckCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case "pending": return "secondary";
      case "in_progress": return "default";
      case "resolved": return "outline";
      default: return "secondary";
    }
  };

  if (loading) {
    return <div className="p-6">Loading complaints...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Complaints Management</h1>
        <p className="text-slate-600 text-lg">Review and manage worker complaints.</p>
      </div>

      <div className="space-y-4">
        {complaints.map((complaint) => (
          <Card key={complaint.id} className="card-hover border-none shadow-sm" data-testid={`complaint-card-${complaint.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <CardTitle className="text-lg">{complaint.title}</CardTitle>
                    <Badge variant={getStatusVariant(complaint.status)} className="flex items-center gap-1">
                      {getStatusIcon(complaint.status)}
                      {complaint.status.replace("_", " ")}
                    </Badge>
                  </div>
                  <CardDescription>
                    <span className="font-medium">{complaint.member_name}</span> • {complaint.member_email}
                  </CardDescription>
                </div>
                <Select
                  value={complaint.status}
                  onValueChange={(value) => updateComplaintStatus(complaint.id, value)}
                  data-testid={`complaint-status-select-${complaint.id}`}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-700">{complaint.description}</p>
              
              {/* Translation Section */}
              {translations[complaint.id] && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Languages className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-medium text-blue-700">English Translation</span>
                  </div>
                  <p className="text-sm text-blue-900">{translations[complaint.id]}</p>
                </div>
              )}
              
              <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span>Category: {complaint.category}</span>
                  <span>•</span>
                  <span>{new Date(complaint.created_at).toLocaleDateString()}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => translateComplaint(complaint.id, complaint.description)}
                  disabled={translating[complaint.id]}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  data-testid={`translate-complaint-${complaint.id}`}
                >
                  <Languages className="w-3 h-3 mr-1" />
                  {translating[complaint.id] ? "Translating..." : translations[complaint.id] ? "Re-translate" : "Translate"}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {complaints.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No complaints yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Pending Approvals View - Super Admin Only
function PendingApprovalsView({ user }) {
  const [pendingMembers, setPendingMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [loadingDetails, setLoadingDetails] = useState(false);

  const fetchPendingMembers = async () => {
    try {
      const response = await axios.get('/members');
      // Filter members who need attention (pending, pending_review, or payment_rejected)
      const pending = response.data.filter(m => 
        m.role === "member" && 
        ["pending", "pending_review", "payment_rejected"].includes(m.subscription_status)
      );
      setPendingMembers(pending);
    } catch (error) {
      toast.error("Failed to fetch pending members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingMembers();
  }, []);

  const handleViewDetails = async (member) => {
    setSelectedMember(member);
    setShowDetailsModal(true);
    setLoadingDetails(true);
    try {
      const response = await axios.get(`/admin/members/${member.id}/payment-details`);
      setPaymentDetails(response.data);
    } catch (error) {
      toast.error("Failed to load payment details");
      setPaymentDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleApprove = async (memberId) => {
    setProcessingId(memberId);
    try {
      await axios.post(`/admin/members/${memberId}/activate`);
      toast.success("Member approved and notified via email!");
      setShowDetailsModal(false);
      fetchPendingMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve member");
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async () => {
    if (!selectedMember) return;
    setProcessingId(selectedMember.id);
    try {
      await axios.post(`/admin/members/${selectedMember.id}/reject`, { reason: rejectReason });
      toast.success("Member rejected and notified via email");
      setShowRejectModal(false);
      setShowDetailsModal(false);
      setRejectReason("");
      fetchPendingMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject member");
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (member) => {
    if (member.subscription_status === "pending_review") {
      return (
        <Badge className="bg-blue-100 text-blue-700">
          <FileText className="w-3 h-3 mr-1" />
          Payment Submitted — Pending Review
        </Badge>
      );
    } else if (member.subscription_status === "payment_rejected") {
      return (
        <Badge className="bg-red-100 text-red-700">
          <X className="w-3 h-3 mr-1" />
          Payment Rejected
        </Badge>
      );
    } else if (member.payment_completed) {
      return (
        <Badge className="bg-emerald-100 text-emerald-700">
          <CheckCircle className="w-3 h-3 mr-1" />
          Paid — Ready for Approval
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-amber-100 text-amber-700">
          <Clock className="w-3 h-3 mr-1" />
          Awaiting Payment
        </Badge>
      );
    }
  };

  if (user?.role !== "super_admin") {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Access Restricted</h2>
          <p className="text-slate-500">Only Super Admin can approve new members.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Separate members by status
  const pendingReview = pendingMembers.filter(m => m.subscription_status === "pending_review");
  const awaitingPayment = pendingMembers.filter(m => m.subscription_status === "pending" && !m.payment_completed);
  const rejectedMembers = pendingMembers.filter(m => m.subscription_status === "payment_rejected");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Pending Member Approvals</h1>
        <p className="text-slate-600 mt-1">Review payment proofs and approve new member registrations</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500 rounded-xl">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-700 font-medium">Payment Submitted</p>
                <p className="text-3xl font-bold text-blue-900">{pendingReview.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-500 rounded-xl">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-amber-700 font-medium">Awaiting Payment</p>
                <p className="text-3xl font-bold text-amber-900">{awaitingPayment.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-500 rounded-xl">
                <X className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-red-700 font-medium">Rejected</p>
                <p className="text-3xl font-bold text-red-900">{rejectedMembers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-500 rounded-xl">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-slate-700 font-medium">Total Pending</p>
                <p className="text-3xl font-bold text-slate-900">{pendingMembers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Submitted - Priority Review */}
      {pendingReview.length > 0 && (
        <Card className="border-blue-200">
          <CardHeader className="bg-blue-50">
            <CardTitle className="text-blue-900 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Payment Proofs to Review ({pendingReview.length})
            </CardTitle>
            <CardDescription>These members have submitted payment proof - click to review and approve/reject</CardDescription>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-3">
              {pendingReview.map((member) => (
                <div 
                  key={member.id} 
                  className="flex items-center justify-between p-4 bg-white rounded-xl border border-blue-200 hover:border-blue-400 transition-colors cursor-pointer"
                  onClick={() => handleViewDetails(member)}
                  data-testid={`pending-member-${member.id}`}
                >
                  <div className="flex items-center gap-4">
                    <Avatar className="h-12 w-12 ring-2 ring-blue-200">
                      <AvatarFallback className="bg-blue-100 text-blue-700 font-semibold">
                        {member.full_name?.charAt(0) || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-slate-900">{member.full_name}</p>
                      <p className="text-sm text-slate-500">{member.email}</p>
                      <div className="mt-1">{getStatusBadge(member)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50">
                      <Eye className="w-4 h-4 mr-2" />
                      View Payment Proof
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Awaiting Payment */}
      {awaitingPayment.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              Awaiting Payment ({awaitingPayment.length})
            </CardTitle>
            <CardDescription>These members have registered but haven't submitted payment yet</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {awaitingPayment.map((member) => (
                <div 
                  key={member.id} 
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200"
                >
                  <div className="flex items-center gap-4">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="bg-amber-100 text-amber-700 font-semibold">
                        {member.full_name?.charAt(0) || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-slate-900">{member.full_name}</p>
                      <p className="text-sm text-slate-500">{member.email}</p>
                      <div className="mt-1">{getStatusBadge(member)}</div>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500">
                    Registered: {new Date(member.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rejected */}
      {rejectedMembers.length > 0 && (
        <Card className="border-red-200">
          <CardHeader className="bg-red-50">
            <CardTitle className="text-red-900 flex items-center gap-2">
              <X className="w-5 h-5" />
              Previously Rejected ({rejectedMembers.length})
            </CardTitle>
            <CardDescription>These members had their payment rejected and may resubmit</CardDescription>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-3">
              {rejectedMembers.map((member) => (
                <div 
                  key={member.id} 
                  className="flex items-center justify-between p-4 bg-white rounded-xl border border-red-200"
                  onClick={() => handleViewDetails(member)}
                >
                  <div className="flex items-center gap-4">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="bg-red-100 text-red-700 font-semibold">
                        {member.full_name?.charAt(0) || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-slate-900">{member.full_name}</p>
                      <p className="text-sm text-slate-500">{member.email}</p>
                      {member.rejection_reason && (
                        <p className="text-xs text-red-600 mt-1">Reason: {member.rejection_reason}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {pendingMembers.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-slate-500">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-300" />
              <p className="text-lg font-medium">All caught up!</p>
              <p className="text-sm">No pending approvals at this time.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Member Payment Details
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : selectedMember && paymentDetails ? (
            <div className="space-y-6">
              {/* Member Info */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="font-semibold text-slate-900 mb-3">Member Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Full Name</p>
                    <p className="font-medium">{selectedMember.full_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Email</p>
                    <p className="font-medium">{selectedMember.email}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Phone</p>
                    <p className="font-medium">{selectedMember.phone || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Nationality</p>
                    <p className="font-medium">{selectedMember.nationality || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Passport</p>
                    <p className="font-medium">{selectedMember.passport_number || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Employer</p>
                    <p className="font-medium">{selectedMember.employer || "N/A"}</p>
                  </div>
                </div>
              </div>

              {/* Payment Proof */}
              {paymentDetails.latest_payment_proof ? (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Payment Proof Submitted
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                    <div>
                      <p className="text-blue-700">Reference Number</p>
                      <p className="font-mono font-bold text-lg">{paymentDetails.latest_payment_proof.reference_number || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-blue-700">Amount</p>
                      <p className="font-bold text-lg">RM {paymentDetails.latest_payment_proof.amount || "20"}</p>
                    </div>
                    <div>
                      <p className="text-blue-700">Payment Method</p>
                      <p className="font-medium capitalize">{paymentDetails.latest_payment_proof.payment_method?.replace("_", " ") || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-blue-700">Submitted At</p>
                      <p className="font-medium">{paymentDetails.latest_payment_proof.submitted_at ? new Date(paymentDetails.latest_payment_proof.submitted_at).toLocaleString() : new Date(paymentDetails.latest_payment_proof.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  
                  {/* Payment Screenshot */}
                  {paymentDetails.latest_payment_proof.proof_image && (
                    <div className="mt-4">
                      <p className="text-blue-700 text-sm mb-2">Payment Screenshot / Banking Slip:</p>
                      <div className="bg-white rounded-lg p-2 border border-blue-200">
                        <img 
                          src={paymentDetails.latest_payment_proof.proof_image} 
                          alt="Payment Proof" 
                          className="max-w-full max-h-96 mx-auto rounded-lg shadow-lg"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                  <p className="text-amber-800 text-center">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-amber-600" />
                    No payment proof submitted yet
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </Button>
                {(selectedMember.subscription_status === "pending_review" || selectedMember.payment_completed) && (
                  <>
                    <Button 
                      variant="destructive"
                      onClick={() => {
                        setShowRejectModal(true);
                      }}
                      disabled={processingId === selectedMember.id}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Reject Payment
                    </Button>
                    <Button 
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => handleApprove(selectedMember.id)}
                      disabled={processingId === selectedMember.id}
                    >
                      {processingId === selectedMember.id ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Processing...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Approve Member
                        </span>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              Failed to load payment details
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <X className="w-5 h-5" />
              Reject Payment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              The member will be notified via email and asked to resubmit payment proof.
            </p>
            <div>
              <Label htmlFor="rejectReason">Rejection Reason (Optional)</Label>
              <Input
                id="rejectReason"
                placeholder="e.g., Payment proof is unclear, amount doesn't match..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowRejectModal(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleReject}
                disabled={processingId}
              >
                {processingId ? "Rejecting..." : "Confirm Rejection"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Payments View
function PaymentsView({ user }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, pending, paid, rejected
  const [selectedProof, setSelectedProof] = useState(null);
  
  // Only super_admin can approve/reject payments
  const canApprovePayments = user?.role === "super_admin";

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get('/admin/payment-transactions');
      setTransactions(response.data);
    } catch (error) {
      toast.error("Failed to load payment transactions");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPayment = async (transactionId, action) => {
    try {
      await axios.post(`/admin/verify-payment/${transactionId}?action=${action}`);
      toast.success(`Payment ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      fetchTransactions();
    } catch (error) {
      toast.error("Failed to process payment");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: "Pending", variant: "secondary", icon: Clock },
      pending_verification: { label: "Awaiting Verification", variant: "warning", icon: AlertTriangle },
      paid: { label: "Paid", variant: "success", icon: CheckCircle },
      completed: { label: "Completed", variant: "success", icon: CheckCircle },
      rejected: { label: "Rejected", variant: "destructive", icon: X },
      expired: { label: "Expired", variant: "secondary", icon: Clock },
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge 
        variant={config.variant === "success" ? "default" : config.variant === "warning" ? "outline" : "secondary"}
        className={`gap-1 ${config.variant === "success" ? "bg-emerald-100 text-emerald-700" : 
                           config.variant === "warning" ? "bg-amber-100 text-amber-700 border-amber-200" : 
                           config.variant === "destructive" ? "bg-red-100 text-red-700" : ""}`}
      >
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      fpx: "Online Banking",
      qr_code: "DuitNow QR",
      bank_transfer: "Bank Transfer"
    };
    return methods[method] || method;
  };

  const filteredTransactions = transactions.filter(t => {
    if (filter === "all") return true;
    if (filter === "pending") return t.payment_status === "pending" || t.payment_status === "pending_verification";
    return t.payment_status === filter;
  });

  if (loading) {
    return <div className="p-6">Loading payment transactions...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Payment Management</h1>
        <p className="text-slate-600 text-lg">Review and verify member payment submissions.</p>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{transactions.filter(t => t.payment_status === "pending_verification").length}</p>
                <p className="text-sm text-slate-500">Pending Verification</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{transactions.filter(t => t.payment_status === "paid").length}</p>
                <p className="text-sm text-slate-500">Paid</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">RM {transactions.filter(t => t.payment_status === "paid").reduce((sum, t) => sum + t.amount, 0).toFixed(0)}</p>
                <p className="text-sm text-slate-500">Total Revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{transactions.length}</p>
                <p className="text-sm text-slate-500">Total Transactions</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Tabs */}
      <Tabs value={filter} onValueChange={setFilter} className="w-full">
        <TabsList>
          <TabsTrigger value="all">All ({transactions.length})</TabsTrigger>
          <TabsTrigger value="pending">
            Pending ({transactions.filter(t => t.payment_status === "pending" || t.payment_status === "pending_verification").length})
          </TabsTrigger>
          <TabsTrigger value="paid">Paid ({transactions.filter(t => t.payment_status === "paid").length})</TabsTrigger>
          <TabsTrigger value="rejected">Rejected ({transactions.filter(t => t.payment_status === "rejected").length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Transactions List */}
      <div className="space-y-4">
        {filteredTransactions.map((transaction) => (
          <Card key={transaction.id} className="border-none shadow-sm" data-testid={`transaction-card-${transaction.id}`}>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="font-semibold text-lg">{transaction.user_name || transaction.user_email}</h3>
                    {getStatusBadge(transaction.payment_status)}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500 block">Amount</span>
                      <span className="font-semibold text-slate-900">RM {transaction.amount}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Plan</span>
                      <span className="font-medium">{transaction.months} {transaction.months === 1 ? 'Month' : 'Months'}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Method</span>
                      <span className="font-medium">{getPaymentMethodLabel(transaction.payment_method)}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Date</span>
                      <span className="font-medium">{new Date(transaction.created_at).toLocaleDateString()}</span>
                    </div>
                    {transaction.reference_number && (
                      <div className="col-span-2">
                        <span className="text-slate-500 block">Reference #</span>
                        <span className="font-mono font-medium">{transaction.reference_number}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {transaction.proof_image && (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" onClick={() => setSelectedProof(transaction.proof_image)}>
                          <FileText className="w-4 h-4 mr-1" />
                          View Proof
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Payment Proof</DialogTitle>
                          <DialogDescription>Uploaded receipt from {transaction.user_name || transaction.user_email}</DialogDescription>
                        </DialogHeader>
                        <div className="flex justify-center p-4">
                          <img src={transaction.proof_image} alt="Payment proof" className="max-w-full max-h-[500px] object-contain rounded-lg" />
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                  
                  {transaction.payment_status === "pending_verification" && canApprovePayments && (
                    <>
                      <Button
                        onClick={() => handleVerifyPayment(transaction.id, "approve")}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        size="sm"
                        data-testid={`approve-payment-${transaction.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleVerifyPayment(transaction.id, "reject")}
                        variant="destructive"
                        size="sm"
                        data-testid={`reject-payment-${transaction.id}`}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </>
                  )}
                  {transaction.payment_status === "pending_verification" && !canApprovePayments && (
                    <span className="text-sm text-amber-600 font-medium">
                      Awaiting Super Admin approval
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredTransactions.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No payment transactions found</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Visa Tracker View
function VisaTrackerView() {
  const [visaData, setVisaData] = useState({ stats: {}, records: [] });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [renewalNotes, setRenewalNotes] = useState("");
  const [sendingAlerts, setSendingAlerts] = useState(false);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [selectedAlertType, setSelectedAlertType] = useState("30_day");

  useEffect(() => {
    fetchVisaData();
  }, []);

  const fetchVisaData = async () => {
    try {
      const response = await axios.get('/visa-tracker');
      setVisaData(response.data);
    } catch (error) {
      toast.error("Failed to load visa data");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRenewal = async (memberId, status) => {
    try {
      await axios.patch(`/visa-tracker/${memberId}`, {
        renewal_notes: renewalNotes,
        renewal_status: status
      });
      toast.success("Renewal status updated");
      setEditingId(null);
      setRenewalNotes("");
      fetchVisaData();
    } catch (error) {
      toast.error("Failed to update renewal");
    }
  };

  const handleSendAlerts = async () => {
    setSendingAlerts(true);
    try {
      const response = await axios.post('/visa-alerts/send', {
        alert_type: selectedAlertType
      });
      const { sent_count, failed_count } = response.data;
      if (sent_count > 0) {
        toast.success(`Sent ${sent_count} alert email(s) successfully!`);
      }
      if (failed_count > 0) {
        toast.error(`Failed to send ${failed_count} email(s)`);
      }
      if (sent_count === 0 && failed_count === 0) {
        toast.info("No workers match the selected criteria");
      }
      setAlertDialogOpen(false);
    } catch (error) {
      toast.error("Failed to send alerts");
    } finally {
      setSendingAlerts(false);
    }
  };

  const handleSendAdminSummary = async () => {
    setSendingAlerts(true);
    try {
      const response = await axios.post('/visa-alerts/send-admin-summary');
      if (response.data.sent) {
        toast.success(`Summary email sent! ${response.data.expiring_count} workers expiring within 90 days.`);
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to send admin summary");
    } finally {
      setSendingAlerts(false);
    }
  };

  const getStatusBadge = (status, daysRemaining) => {
    switch (status) {
      case "expired":
        return <Badge variant="destructive" className="bg-red-100 text-red-700 hover:bg-red-100">Expired</Badge>;
      case "expiring_soon":
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">{daysRemaining} days left</Badge>;
      case "valid":
        return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Valid</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const filteredRecords = visaData.records.filter(record => 
    record.member_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    record.permit_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (record.email && record.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return <div className="p-6">Loading visa information...</div>;
  }

  const { stats } = visaData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Visa & Permit Tracker</h1>
          <p className="text-slate-600 text-lg">Monitor and manage worker visa renewals.</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-64">
            <Input
              placeholder="Search by name, ID, or permit..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-white"
              data-testid="visa-search-input"
            />
          </div>
          <Dialog open={alertDialogOpen} onOpenChange={setAlertDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="send-alerts-button">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Send Alerts
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Send Visa Expiry Alerts</DialogTitle>
                <DialogDescription>
                  Send email notifications to workers with expiring visas.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Alert Type</Label>
                  <Select value={selectedAlertType} onValueChange={setSelectedAlertType}>
                    <SelectTrigger data-testid="alert-type-select">
                      <SelectValue placeholder="Select alert type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30_day">30-Day Warning (Urgent)</SelectItem>
                      <SelectItem value="60_day">60-Day Reminder</SelectItem>
                      <SelectItem value="90_day">90-Day Notice</SelectItem>
                      <SelectItem value="all_expiring">All Expiring (within 90 days)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-600">
                    <strong>30-Day Warning:</strong> {stats.expiring_soon || 0} workers<br/>
                    <strong>Total Expiring (90 days):</strong> {(stats.expiring_soon || 0) + Math.floor((stats.valid_visas || 0) * 0.1)} workers (approx)
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleSendAlerts} 
                    disabled={sendingAlerts}
                    className="flex-1"
                    data-testid="confirm-send-alerts"
                  >
                    {sendingAlerts ? "Sending..." : "Send to Workers"}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handleSendAdminSummary}
                    disabled={sendingAlerts}
                    data-testid="send-admin-summary"
                  >
                    Send Me Summary
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-none shadow-sm bg-emerald-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-emerald-700 font-medium">Valid Visas</p>
                <p className="text-3xl font-bold text-emerald-800">{stats.valid_visas || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-amber-700 font-medium">Expiring Soon</p>
                <p className="text-3xl font-bold text-amber-800">{stats.expiring_soon || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-red-700 font-medium">Expired</p>
                <p className="text-3xl font-bold text-red-800">{stats.expired || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-orange-700 font-medium">Pending Renewal</p>
                <p className="text-3xl font-bold text-orange-800">{stats.pending_renewal || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Visa Table */}
      <Card className="border-none shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="visa-tracker-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-4 px-6 font-semibold text-sm text-slate-700">Member</th>
                  <th className="text-left py-4 px-6 font-semibold text-sm text-slate-700">Permit No.</th>
                  <th className="text-left py-4 px-6 font-semibold text-sm text-slate-700">Expiry Date</th>
                  <th className="text-left py-4 px-6 font-semibold text-sm text-slate-700">Status</th>
                  <th className="text-left py-4 px-6 font-semibold text-sm text-slate-700">Renewal Notes</th>
                  <th className="text-left py-4 px-6 font-semibold text-sm text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((record) => (
                  <tr key={record.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`visa-row-${record.id}`}>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-slate-200 text-slate-700 font-semibold text-sm">
                            {record.member_name.split(" ").map(n => n[0]).join("").substring(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-slate-900">{record.member_name}</p>
                          <p className="text-xs text-slate-500">{record.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="font-mono text-sm text-slate-700">{record.permit_number}</span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-sm text-slate-700">{record.expiry_date}</span>
                    </td>
                    <td className="py-4 px-6">
                      {getStatusBadge(record.status, record.days_remaining)}
                    </td>
                    <td className="py-4 px-6">
                      {editingId === record.id ? (
                        <Input
                          value={renewalNotes}
                          onChange={(e) => setRenewalNotes(e.target.value)}
                          placeholder="Add notes..."
                          className="text-sm"
                          data-testid={`renewal-notes-input-${record.id}`}
                        />
                      ) : (
                        <span className="text-sm text-slate-600">{record.renewal_notes || "-"}</span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex gap-2">
                        {editingId === record.id ? (
                          <>
                            <Button 
                              size="sm" 
                              onClick={() => handleUpdateRenewal(record.id, "pending")}
                              className="bg-blue-600 hover:bg-blue-700"
                              data-testid={`save-renewal-${record.id}`}
                            >
                              Save
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => { setEditingId(null); setRenewalNotes(""); }}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => { setEditingId(record.id); setRenewalNotes(record.renewal_notes || ""); }}
                              data-testid={`edit-renewal-${record.id}`}
                            >
                              Edit
                            </Button>
                            {record.status !== "valid" && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-amber-600 border-amber-300 hover:bg-amber-50"
                                onClick={() => handleUpdateRenewal(record.id, "pending")}
                                data-testid={`mark-pending-${record.id}`}
                              >
                                Mark Pending
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredRecords.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No visa records found matching your search.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Analytics View - COMPREHENSIVE
function AnalyticsView({ stats }) {
  const [timeFilter, setTimeFilter] = useState("all-time");
  const [detailedStats, setDetailedStats] = useState(null);

  useEffect(() => {
    fetchDetailedStats();
  }, []);

  const fetchDetailedStats = async () => {
    try {
      const [membersRes, complaintsRes, paymentsRes, assignmentsRes] = await Promise.all([
        axios.get('/members'),
        axios.get('/complaints'),
        axios.get('/payments'),
        axios.get('/assignments').catch(() => ({ data: [] }))
      ]);

      const members = membersRes.data;
      const complaints = complaintsRes.data;
      const payments = paymentsRes.data;
      const assignments = assignmentsRes.data;

      // Calculate visa stats
      const now = new Date();
      const expiringVisas = members.filter(m => {
        if (!m.visa_expiry) return false;
        const expiry = new Date(m.visa_expiry);
        const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
        return diffDays > 0 && diffDays <= 30;
      }).length;

      const expiredVisas = members.filter(m => {
        if (!m.visa_expiry) return false;
        const expiry = new Date(m.visa_expiry);
        return expiry < now;
      }).length;

      // Calculate case stats
      const openCases = assignments.filter(a => a.case_status === 'open').length;
      const closedCases = assignments.filter(a => a.case_status === 'closed').length;

      // Calculate payment stats
      const activePayments = payments.filter(p => p.status === 'verified').length;
      const overduePayments = 0; // Placeholder

      const totalRevenue = members.reduce((sum, m) => sum + (m.monthly_fee || 0), 0);
      const collectedRevenue = payments.filter(p => p.status === 'verified').reduce((sum, p) => sum + p.amount, 0);
      const overdueRevenue = 0; // Placeholder

      setDetailedStats({
        totalWorkers: members.length,
        approved: members.filter(m => m.subscription_status === 'active').length,
        pendingApproval: 0,
        totalComplaints: complaints.length,
        openComplaints: complaints.filter(c => c.status !== 'resolved').length,
        resolvedComplaints: complaints.filter(c => c.status === 'resolved').length,
        expiringVisas,
        expiredVisas,
        missingDocs: 0,
        activePayments,
        overduePayments,
        totalRevenue,
        collectedRevenue,
        overdueRevenue,
        collectionRate: totalRevenue > 0 ? Math.round((collectedRevenue / totalRevenue) * 100) : 0,
        openCases,
        closedCases,
        totalCases: assignments.length
      });
    } catch (error) {
      console.error('Failed to fetch detailed stats');
    }
  };

  if (!detailedStats) {
    return <div className="p-6">Loading analytics...</div>;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Analytics Dashboard</h1>
          <p className="text-slate-600 text-lg">Comprehensive overview of system metrics and performance.</p>
        </div>
        <Tabs value={timeFilter} onValueChange={setTimeFilter} className="w-auto">
          <TabsList>
            <TabsTrigger value="today">Today</TabsTrigger>
            <TabsTrigger value="week">This Week</TabsTrigger>
            <TabsTrigger value="month">This Month</TabsTrigger>
            <TabsTrigger value="all-time">All Time</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/admin/members">
          <Card className="border-none shadow-sm cursor-pointer card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Manage Members</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/complaints">
          <Card className="border-none shadow-sm cursor-pointer card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Review Complaints</p>
                  <p className="text-xs text-slate-500">{detailedStats.totalComplaints} items</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/payments">
          <Card className="border-none shadow-sm cursor-pointer card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Process Payments</p>
                  <p className="text-xs text-slate-500">0 items</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/visa-tracker">
          <Card className="border-none shadow-sm cursor-pointer card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Visa Renewals</p>
                  <p className="text-xs text-slate-500">0 items</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Primary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-blue-600" />
              <p className="text-sm text-slate-600">Total Workers</p>
            </div>
            <p className="text-3xl font-bold text-slate-900">{detailedStats.totalWorkers}</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-5 h-5 text-teal-600" />
              <p className="text-sm text-slate-600">Approved</p>
            </div>
            <p className="text-3xl font-bold text-slate-900">{detailedStats.approved}</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-5 h-5 text-orange-600" />
              <p className="text-sm text-slate-600">Pending Approval</p>
            </div>
            <p className="text-3xl font-bold text-slate-900">{detailedStats.pendingApproval}</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <CreditCard className="w-5 h-5 text-green-600" />
              <p className="text-sm text-slate-600">Active Payments</p>
            </div>
            <p className="text-3xl font-bold text-slate-900">{detailedStats.activePayments}</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-sm text-slate-600">Overdue Payments</p>
            </div>
            <p className="text-3xl font-bold text-slate-900">{detailedStats.overduePayments}</p>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <p className="text-sm text-slate-600">Open Complaints</p>
            </div>
            <p className="text-3xl font-bold text-slate-900">{detailedStats.openComplaints}</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-5 h-5 text-orange-600" />
              <p className="text-sm text-slate-600">Expiring Visas</p>
            </div>
            <p className="text-3xl font-bold text-slate-900">{detailedStats.expiringVisas}</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-5 h-5 text-red-600" />
              <p className="text-sm text-slate-600">Expired Visas</p>
            </div>
            <p className="text-3xl font-bold text-slate-900">{detailedStats.expiredVisas}</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <File className="w-5 h-5 text-orange-600" />
              <p className="text-sm text-slate-600">Missing Docs</p>
            </div>
            <p className="text-3xl font-bold text-slate-900">{detailedStats.missingDocs}</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <p className="text-sm text-slate-600">Monthly Revenue</p>
            </div>
            <p className="text-3xl font-bold text-slate-900">RM{detailedStats.totalRevenue}</p>
          </CardContent>
        </Card>
      </div>

      {/* Case Statistics */}
      <div>
        <div className="flex items-center gap-3 mb-6">
          <Briefcase className="w-8 h-8 text-blue-600" />
          <h2 className="text-3xl font-bold text-slate-900">Case Management</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-none shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <Briefcase className="w-5 h-5 text-blue-600" />
                <p className="text-sm text-slate-600">Total Cases</p>
              </div>
              <p className="text-4xl font-bold text-slate-900">{detailedStats.totalCases || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-none shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-5 h-5 text-amber-600" />
                <p className="text-sm text-amber-600">Open Cases</p>
              </div>
              <p className="text-4xl font-bold text-amber-600">{detailedStats.openCases || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-none shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <p className="text-sm text-emerald-600">Closed Cases</p>
              </div>
              <p className="text-4xl font-bold text-emerald-600">{detailedStats.closedCases || 0}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Revenue Summary */}
      <div>
        <div className="flex items-center gap-3 mb-6">
          <DollarSign className="w-8 h-8 text-emerald-600" />
          <h2 className="text-3xl font-bold text-slate-900">Revenue Summary</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="border-none shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-slate-600" />
                <p className="text-sm text-slate-600">Expected Revenue</p>
              </div>
              <p className="text-4xl font-bold text-slate-900">RM{detailedStats.totalRevenue}</p>
            </CardContent>
          </Card>
          <Card className="border-none shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <p className="text-sm text-emerald-600">Collected</p>
              </div>
              <p className="text-4xl font-bold text-emerald-600">RM{detailedStats.collectedRevenue}</p>
            </CardContent>
          </Card>
          <Card className="border-none shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <p className="text-sm text-red-600">Overdue</p>
              </div>
              <p className="text-4xl font-bold text-red-600">RM{detailedStats.overdueRevenue}</p>
            </CardContent>
          </Card>
          <Card className="border-none shadow-sm">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-600 mb-2">Collection Rate</p>
              <p className="text-4xl font-bold text-slate-900">{detailedStats.collectionRate}%</p>
              <div className="mt-4 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-600" style={{width: `${detailedStats.collectionRate}%`}}></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle>Workers by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center text-slate-500">
              <p>No data available</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle>Payments by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center text-slate-500">
              <p>No data available</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle>Complaints by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center text-slate-500">
              <p>No data available</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle>Visa/Permit Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center text-slate-500">
              <p>No data available</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm md:col-span-2">
          <CardHeader>
            <CardTitle>Document Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center text-slate-500">
              <p>No data available</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Live Chat View
function LiveChatView() {
  const [chatRooms, setChatRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  useEffect(() => {
    fetchChatRooms();
    fetchSchedulerStatus();
  }, []);

  useEffect(() => {
    if (selectedRoom) {
      fetchMessages(selectedRoom.id);
    }
  }, [selectedRoom]);

  const fetchChatRooms = async () => {
    try {
      const response = await axios.get('/admin/chat/all-rooms');
      setChatRooms(response.data);
      if (response.data.length > 0) {
        setSelectedRoom(response.data[0]);
      }
    } catch (error) {
      toast.error("Failed to load chat rooms");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (roomId) => {
    try {
      const response = await axios.get(`/admin/chat/rooms/${roomId}/messages`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error("Failed to load messages");
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const response = await axios.get('/scheduler/status');
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch scheduler status");
    }
  };

  const handleStartScheduler = async () => {
    try {
      await axios.post('/scheduler/start');
      toast.success("Visa alert scheduler started!");
      fetchSchedulerStatus();
    } catch (error) {
      toast.error("Failed to start scheduler");
    }
  };

  const handleStopScheduler = async () => {
    try {
      await axios.post('/scheduler/stop');
      toast.success("Scheduler stopped");
      fetchSchedulerStatus();
    } catch (error) {
      toast.error("Failed to stop scheduler");
    }
  };

  const handleRunNow = async () => {
    try {
      const response = await axios.post('/scheduler/run-now');
      toast.success(`Visa check complete: ${response.data.alerts_sent} alerts sent`);
    } catch (error) {
      toast.error("Failed to run visa check");
    }
  };

  if (loading) {
    return <div className="p-6">Loading chat oversight...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Chat Oversight</h1>
          <p className="text-slate-600 text-lg">Monitor all staff-worker conversations.</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={schedulerStatus?.scheduler_running ? "default" : "secondary"} className="py-1 px-3">
            {schedulerStatus?.scheduler_running ? "Scheduler Running" : "Scheduler Stopped"}
          </Badge>
          {schedulerStatus?.scheduler_running ? (
            <Button variant="outline" onClick={handleStopScheduler} data-testid="stop-scheduler-btn">
              Stop Scheduler
            </Button>
          ) : (
            <Button onClick={handleStartScheduler} data-testid="start-scheduler-btn">
              Start Scheduler
            </Button>
          )}
          <Button variant="outline" onClick={handleRunNow} data-testid="run-visa-check-btn">
            Run Visa Check Now
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 h-[600px]">
        {/* Chat Rooms List */}
        <Card className="col-span-4 border-none shadow-sm overflow-hidden">
          <CardHeader className="border-b bg-slate-50">
            <CardTitle className="text-base">Active Conversations ({chatRooms.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-y-auto max-h-[520px]">
            <div className="divide-y">
              {chatRooms.map((room) => (
                <button
                  key={room.id}
                  onClick={() => setSelectedRoom(room)}
                  className={`w-full p-4 text-left hover:bg-slate-50 transition ${
                    selectedRoom?.id === room.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                  }`}
                  data-testid={`oversight-room-${room.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-emerald-600 text-white font-semibold text-xs">
                          {room.member?.name?.split(" ").map(n => n[0]).join("").substring(0, 2) || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm">{room.member?.name}</p>
                        <p className="text-xs text-slate-500">{room.member?.email}</p>
                      </div>
                    </div>
                    <Badge variant={room.case_status === "open" ? "default" : "secondary"} className="text-xs">
                      {room.case_status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Users className="w-3 h-3" />
                    <span>Staff: {room.staff?.name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                    <MessageSquare className="w-3 h-3" />
                    <span>{room.message_count} messages</span>
                  </div>
                  {room.last_message && (
                    <p className="text-xs text-slate-400 mt-2 truncate">
                      Last: {room.last_message.content}
                    </p>
                  )}
                </button>
              ))}
              {chatRooms.length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No active conversations</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Chat Messages View */}
        <Card className="col-span-8 border-none shadow-sm flex flex-col overflow-hidden">
          {selectedRoom ? (
            <>
              <CardHeader className="border-b bg-slate-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-emerald-600 text-white font-semibold">
                        {selectedRoom.member?.name?.split(" ").map(n => n[0]).join("").substring(0, 2) || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <CardTitle className="text-lg">{selectedRoom.member?.name}</CardTitle>
                      <p className="text-sm text-slate-500">
                        Chatting with {selectedRoom.staff?.name}
                      </p>
                    </div>
                  </div>
                  <div className="text-right text-xs text-slate-500">
                    <p>Case ID: {selectedRoom.id.substring(0, 8)}...</p>
                    <p>{messages.length} messages</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50" data-testid="oversight-messages">
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-slate-500">
                    <p>No messages in this conversation yet.</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_role === 'staff' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-2xl p-3 ${
                          msg.sender_role === 'staff'
                            ? 'bg-blue-600 text-white rounded-br-md'
                            : 'bg-white border text-slate-900 rounded-bl-md'
                        }`}
                      >
                        <p className={`text-xs font-semibold mb-1 ${msg.sender_role === 'staff' ? 'text-blue-100' : 'text-slate-500'}`}>
                          {msg.sender_name} ({msg.sender_role})
                        </p>
                        <p className="text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 ${msg.sender_role === 'staff' ? 'text-blue-200' : 'text-slate-400'}`}>
                          {new Date(msg.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
              <div className="border-t p-4 bg-amber-50">
                <p className="text-sm text-amber-700 text-center">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  View-only mode - Admins cannot send messages in worker-staff conversations
                </p>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-slate-500">Select a conversation to view messages</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}


// Reports View
function ReportsView() {
  const [reports, setReports] = useState([]);
  const [generating, setGenerating] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState("pdf");

  useEffect(() => {
    fetchAvailableReports();
  }, []);

  const fetchAvailableReports = async () => {
    try {
      const response = await axios.get('/reports/available');
      setReports(response.data.reports || []);
    } catch (error) {
      toast.error("Failed to load reports");
    }
  };

  const handleGenerateReport = async (reportType) => {
    setGenerating(reportType);
    try {
      const response = await axios.post('/reports/generate', {
        report_type: reportType,
        format: selectedFormat
      }, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const extension = selectedFormat === 'pdf' ? 'pdf' : 'xlsx';
      link.setAttribute('download', `${reportType}_report_${new Date().toISOString().slice(0,10)}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Report downloaded successfully!");
    } catch (error) {
      toast.error("Failed to generate report");
    } finally {
      setGenerating(null);
    }
  };

  const getReportIcon = (reportId) => {
    switch (reportId) {
      case 'workers': return <Users className="w-6 h-6" />;
      case 'visa_status': return <FileText className="w-6 h-6" />;
      case 'complaints': return <AlertCircle className="w-6 h-6" />;
      case 'payments': return <DollarSign className="w-6 h-6" />;
      case 'comprehensive': return <BarChart3 className="w-6 h-6" />;
      default: return <File className="w-6 h-6" />;
    }
  };

  const getReportColor = (reportId) => {
    switch (reportId) {
      case 'workers': return 'bg-blue-100 text-blue-600';
      case 'visa_status': return 'bg-amber-100 text-amber-600';
      case 'complaints': return 'bg-red-100 text-red-600';
      case 'payments': return 'bg-emerald-100 text-emerald-600';
      case 'comprehensive': return 'bg-purple-100 text-purple-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Reports</h1>
          <p className="text-slate-600 text-lg">Download detailed reports in PDF or Excel format.</p>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-lg border p-1">
          <button
            onClick={() => setSelectedFormat('pdf')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${
              selectedFormat === 'pdf' 
                ? 'bg-slate-900 text-white' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="format-pdf-btn"
          >
            <FileText className="w-4 h-4 inline mr-2" />
            PDF
          </button>
          <button
            onClick={() => setSelectedFormat('excel')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${
              selectedFormat === 'excel' 
                ? 'bg-slate-900 text-white' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="format-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4 inline mr-2" />
            Excel
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reports.map((report) => (
          <Card key={report.id} className="border-none shadow-sm hover:shadow-md transition" data-testid={`report-card-${report.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${getReportColor(report.id)}`}>
                  {getReportIcon(report.id)}
                </div>
              </div>
              <CardTitle className="text-lg mt-4">{report.name}</CardTitle>
              <CardDescription>{report.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={() => handleGenerateReport(report.id)}
                disabled={generating === report.id}
                className="w-full"
                data-testid={`generate-${report.id}-btn`}
              >
                {generating === report.id ? (
                  <>
                    <Clock className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download {selectedFormat.toUpperCase()}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {reports.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Loading available reports...</p>
        </div>
      )}
    </div>
  );
}


// Announcements Management View
function AnnouncementsManagementView() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    category: 'info',
    image_url: '',
    video_url: '',
    is_pinned: false,
    expiry_date: ''
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get('/announcements');
      setAnnouncements(response.data);
    } catch (error) {
      toast.error('Failed to fetch announcements');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      body: '',
      category: 'info',
      image_url: '',
      video_url: '',
      is_pinned: false,
      expiry_date: ''
    });
    setEditingAnnouncement(null);
  };

  const handleCreateAnnouncement = async () => {
    if (!formData.title || !formData.body) {
      toast.error('Title and content are required');
      return;
    }

    try {
      await axios.post('/announcements', formData);
      toast.success('Announcement created!');
      fetchAnnouncements();
      setShowCreateDialog(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const handleUpdateAnnouncement = async () => {
    if (!formData.title || !formData.body) {
      toast.error('Title and content are required');
      return;
    }

    try {
      await axios.patch(`/announcements/${editingAnnouncement.id}`, formData);
      toast.success('Announcement updated!');
      fetchAnnouncements();
      setShowCreateDialog(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to update announcement');
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!confirm('Are you sure you want to delete this announcement?')) return;
    
    try {
      await axios.delete(`/announcements/${id}`);
      toast.success('Announcement deleted!');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const handleTogglePin = async (id) => {
    try {
      await axios.post(`/announcements/${id}/toggle-pin`);
      fetchAnnouncements();
      toast.success('Pin status updated!');
    } catch (error) {
      toast.error('Failed to update pin status');
    }
  };

  const handleTogglePublish = async (id) => {
    try {
      await axios.post(`/announcements/${id}/toggle-publish`);
      fetchAnnouncements();
      toast.success('Publish status updated!');
    } catch (error) {
      toast.error('Failed to update publish status');
    }
  };

  const openEditDialog = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      body: announcement.body,
      category: announcement.category,
      image_url: announcement.image_url || '',
      video_url: announcement.video_url || '',
      is_pinned: announcement.is_pinned || false,
      expiry_date: announcement.expiry_date || ''
    });
    setShowCreateDialog(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post('/announcements/upload-media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.type === 'video') {
        setFormData(prev => ({ ...prev, video_url: response.data.url }));
      } else {
        setFormData(prev => ({ ...prev, image_url: response.data.url }));
      }
      toast.success('Media uploaded!');
    } catch (error) {
      toast.error('Failed to upload media');
    } finally {
      setUploading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      warning: 'bg-red-100 text-red-700 border-red-200',
      info: 'bg-blue-100 text-blue-700 border-blue-200',
      update: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      video: 'bg-purple-100 text-purple-700 border-purple-200'
    };
    return colors[category] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const getCategoryBorderColor = (category) => {
    const colors = {
      warning: 'border-l-red-500',
      info: 'border-l-blue-500',
      update: 'border-l-emerald-500',
      video: 'border-l-purple-500'
    };
    return colors[category] || 'border-l-slate-500';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="announcements-management-view">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Announcements Management</h1>
          <p className="text-slate-500">Create and manage announcements for workers</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-announcement-btn">
              <Plus className="w-4 h-4" />
              Create Announcement
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}</DialogTitle>
              <DialogDescription>
                {editingAnnouncement ? 'Update the announcement details' : 'Create a new announcement to inform workers'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter announcement title"
                  data-testid="announcement-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Content *</Label>
                <Textarea
                  value={formData.body}
                  onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                  placeholder="Enter announcement content"
                  rows={5}
                  data-testid="announcement-body-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={formData.category} onValueChange={(val) => setFormData(prev => ({ ...prev, category: val }))}>
                    <SelectTrigger data-testid="announcement-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="info">Information</SelectItem>
                      <SelectItem value="warning">Warning</SelectItem>
                      <SelectItem value="update">Update</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Expiry Date (Optional)</Label>
                  <Input
                    type="datetime-local"
                    value={formData.expiry_date ? formData.expiry_date.slice(0, 16) : ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
                    data-testid="announcement-expiry-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>YouTube URL (Optional)</Label>
                <Input
                  value={formData.video_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                  placeholder="https://www.youtube.com/watch?v=..."
                  data-testid="announcement-video-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Image URL (Optional)</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                  placeholder="Enter image URL or upload below"
                  data-testid="announcement-image-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Upload Media</Label>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center">
                  <input
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="media-upload"
                    disabled={uploading}
                  />
                  <label htmlFor="media-upload" className="cursor-pointer">
                    {uploading ? (
                      <span className="text-slate-500">Uploading...</span>
                    ) : (
                      <span className="text-blue-600 hover:text-blue-700">Click to upload image or video</span>
                    )}
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_pinned"
                  checked={formData.is_pinned}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_pinned: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-300"
                />
                <Label htmlFor="is_pinned">Pin this announcement to the top</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
                Cancel
              </Button>
              <Button onClick={editingAnnouncement ? handleUpdateAnnouncement : handleCreateAnnouncement}>
                {editingAnnouncement ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {announcements.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Megaphone className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="font-semibold text-slate-900 mb-2">No announcements yet</h3>
            <p className="text-slate-500 mb-4">Create your first announcement to keep workers informed</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Announcement
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {announcements.map((announcement) => (
            <Card key={announcement.id} className={`border-l-4 ${getCategoryBorderColor(announcement.category)}`} data-testid={`admin-announcement-${announcement.id}`}>
              <CardContent className="p-4">
                <div className="flex gap-4">
                  {/* Thumbnail */}
                  {(announcement.image_url || announcement.video_url) && (
                    <div className="w-24 h-24 flex-shrink-0 bg-slate-100 rounded-lg overflow-hidden">
                      {announcement.video_url && announcement.video_url.includes('youtube') ? (
                        <img 
                          src={`https://img.youtube.com/vi/${announcement.video_url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1]}/mqdefault.jpg`}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : announcement.image_url ? (
                        <img src={announcement.image_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="w-8 h-8 text-slate-400" />
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge className={getCategoryColor(announcement.category)}>
                        {announcement.category}
                      </Badge>
                      {announcement.is_pinned && (
                        <Badge variant="outline" className="gap-1">
                          <Pin className="w-3 h-3" /> Pinned
                        </Badge>
                      )}
                      {announcement.is_published ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Published</Badge>
                      ) : (
                        <Badge variant="secondary">Draft</Badge>
                      )}
                      {announcement.expiry_date && new Date(announcement.expiry_date) < new Date() && (
                        <Badge className="bg-amber-100 text-amber-700">Expired</Badge>
                      )}
                    </div>
                    <h3 className="font-bold text-slate-900 mb-1">{announcement.title}</h3>
                    <p className="text-sm text-slate-500 mb-2">
                      Posted {formatDate(announcement.created_at)} • {announcement.read_count} views
                    </p>
                    <p className="text-sm text-slate-600 line-clamp-2">{announcement.body}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEditDialog(announcement)} className="gap-1">
                      <Edit className="w-4 h-4" /> Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleTogglePin(announcement.id)}
                      className="gap-1"
                    >
                      <Pin className="w-4 h-4" /> 
                      {announcement.is_pinned ? 'Unpin' : 'Pin'}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleTogglePublish(announcement.id)}
                      className="gap-1"
                    >
                      {announcement.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      {announcement.is_published ? 'Unpublish' : 'Publish'}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleDeleteAnnouncement(announcement.id)}
                      className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" /> Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


// Jobs Management View
function JobsManagementView() {
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showApplicantsDialog, setShowApplicantsDialog] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [activeTab, setActiveTab] = useState('listings');
  const [formData, setFormData] = useState({
    title: '',
    company_name: '',
    location: '',
    salary_min: '',
    salary_max: '',
    job_type: 'full_time',
    industry: 'general',
    description: '',
    requirements: '',
    application_deadline: ''
  });

  useEffect(() => {
    fetchJobs();
    fetchAllApplications();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get('/jobs');
      setJobs(response.data);
    } catch (error) {
      toast.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllApplications = async () => {
    try {
      const response = await axios.get('/job-applications');
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch applications');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      company_name: '',
      location: '',
      salary_min: '',
      salary_max: '',
      job_type: 'full_time',
      industry: 'general',
      description: '',
      requirements: '',
      application_deadline: ''
    });
    setEditingJob(null);
  };

  const handleCreateJob = async () => {
    if (!formData.title || !formData.company_name || !formData.location || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const payload = {
        ...formData,
        salary_min: formData.salary_min ? parseFloat(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseFloat(formData.salary_max) : null,
        application_deadline: formData.application_deadline ? new Date(formData.application_deadline).toISOString() : null
      };
      await axios.post('/jobs', payload);
      toast.success('Job created successfully!');
      fetchJobs();
      setShowCreateDialog(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to create job');
    }
  };

  const handleUpdateJob = async () => {
    if (!formData.title || !formData.company_name || !formData.location || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const payload = {
        ...formData,
        salary_min: formData.salary_min ? parseFloat(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseFloat(formData.salary_max) : null,
        application_deadline: formData.application_deadline ? new Date(formData.application_deadline).toISOString() : null
      };
      await axios.patch(`/jobs/${editingJob.id}`, payload);
      toast.success('Job updated successfully!');
      fetchJobs();
      setShowCreateDialog(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to update job');
    }
  };

  const handleDeleteJob = async (id) => {
    if (!confirm('Are you sure you want to delete this job? All applications will also be deleted.')) return;
    
    try {
      await axios.delete(`/jobs/${id}`);
      toast.success('Job deleted successfully!');
      fetchJobs();
      fetchAllApplications();
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const handleTogglePublish = async (id) => {
    try {
      await axios.post(`/jobs/${id}/toggle-publish`);
      fetchJobs();
      toast.success('Publish status updated!');
    } catch (error) {
      toast.error('Failed to update publish status');
    }
  };

  const openEditDialog = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title,
      company_name: job.company_name,
      location: job.location,
      salary_min: job.salary_min || '',
      salary_max: job.salary_max || '',
      job_type: job.job_type,
      industry: job.industry,
      description: job.description,
      requirements: job.requirements || '',
      application_deadline: job.application_deadline ? job.application_deadline.slice(0, 16) : ''
    });
    setShowCreateDialog(true);
  };

  const openApplicantsDialog = (job) => {
    setSelectedJob(job);
    setShowApplicantsDialog(true);
  };

  const handleUpdateApplicationStatus = async (appId, newStatus) => {
    try {
      await axios.patch(`/job-applications/${appId}`, { status: newStatus });
      toast.success('Application status updated!');
      fetchAllApplications();
    } catch (error) {
      toast.error('Failed to update application status');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      submitted: 'bg-blue-100 text-blue-700',
      reviewing: 'bg-yellow-100 text-yellow-700',
      interview: 'bg-purple-100 text-purple-700',
      accepted: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      submitted: 'Submitted',
      reviewing: 'Under Review',
      interview: 'Interview',
      accepted: 'Accepted',
      rejected: 'Rejected'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getJobApplications = (jobId) => {
    return applications.filter(app => app.job_id === jobId);
  };

  const isJobExpired = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="jobs-management-view">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Jobs Management</h1>
          <p className="text-slate-500">Create and manage job postings for workers</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-job-btn">
              <Plus className="w-4 h-4" />
              Create Job Posting
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingJob ? 'Edit Job Posting' : 'Create Job Posting'}</DialogTitle>
              <DialogDescription>
                {editingJob ? 'Update the job details' : 'Create a new job posting for workers to apply'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Job Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Construction Worker, Factory Operator"
                  data-testid="job-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Company name"
                    data-testid="job-company-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Location *</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="e.g., Kuala Lumpur, Penang"
                    data-testid="job-location-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Minimum Salary (RM)</Label>
                  <Input
                    type="number"
                    value={formData.salary_min}
                    onChange={(e) => setFormData(prev => ({ ...prev, salary_min: e.target.value }))}
                    placeholder="e.g., 1500"
                    data-testid="job-salary-min-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Maximum Salary (RM)</Label>
                  <Input
                    type="number"
                    value={formData.salary_max}
                    onChange={(e) => setFormData(prev => ({ ...prev, salary_max: e.target.value }))}
                    placeholder="e.g., 2500"
                    data-testid="job-salary-max-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Job Type</Label>
                  <Select value={formData.job_type} onValueChange={(val) => setFormData(prev => ({ ...prev, job_type: val }))}>
                    <SelectTrigger data-testid="job-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Full Time</SelectItem>
                      <SelectItem value="part_time">Part Time</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                      <SelectItem value="temporary">Temporary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Industry</Label>
                  <Select value={formData.industry} onValueChange={(val) => setFormData(prev => ({ ...prev, industry: val }))}>
                    <SelectTrigger data-testid="job-industry-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="construction">Construction</SelectItem>
                      <SelectItem value="manufacturing">Manufacturing</SelectItem>
                      <SelectItem value="agriculture">Agriculture</SelectItem>
                      <SelectItem value="hospitality">Hospitality</SelectItem>
                      <SelectItem value="services">Services</SelectItem>
                      <SelectItem value="logistics">Logistics</SelectItem>
                      <SelectItem value="retail">Retail</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Application Deadline</Label>
                <Input
                  type="datetime-local"
                  value={formData.application_deadline}
                  onChange={(e) => setFormData(prev => ({ ...prev, application_deadline: e.target.value }))}
                  data-testid="job-deadline-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Job Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the job responsibilities, working hours, benefits..."
                  rows={4}
                  data-testid="job-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Requirements</Label>
                <Textarea
                  value={formData.requirements}
                  onChange={(e) => setFormData(prev => ({ ...prev, requirements: e.target.value }))}
                  placeholder="Required qualifications, visa type, experience, nationality..."
                  rows={3}
                  data-testid="job-requirements-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
                Cancel
              </Button>
              <Button onClick={editingJob ? handleUpdateJob : handleCreateJob} data-testid="job-submit-btn">
                {editingJob ? 'Update Job' : 'Create Job'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs for Listings and All Applications */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="listings" className="gap-2">
            <Briefcase className="w-4 h-4" />
            Job Listings ({jobs.length})
          </TabsTrigger>
          <TabsTrigger value="applications" className="gap-2">
            <Users className="w-4 h-4" />
            All Applications ({applications.length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {activeTab === 'listings' && (
        <>
          {jobs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Briefcase className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h3 className="font-semibold text-slate-900 mb-2">No job postings yet</h3>
                <p className="text-slate-500 mb-4">Create your first job posting for workers to apply</p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Job Posting
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => {
                const jobApps = getJobApplications(job.id);
                const expired = isJobExpired(job.application_deadline);
                return (
                  <Card key={job.id} className={`border-l-4 ${job.is_published ? 'border-l-emerald-500' : 'border-l-slate-300'}`} data-testid={`admin-job-${job.id}`}>
                    <CardContent className="p-4">
                      <div className="flex gap-4 flex-wrap md:flex-nowrap">
                        {/* Job Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            {job.is_published ? (
                              <Badge className="bg-emerald-100 text-emerald-700">Published</Badge>
                            ) : (
                              <Badge variant="secondary">Unpublished</Badge>
                            )}
                            {expired && (
                              <Badge className="bg-amber-100 text-amber-700">Expired</Badge>
                            )}
                            <Badge variant="outline">{job.job_type.replace('_', ' ')}</Badge>
                            <Badge variant="outline">{job.industry}</Badge>
                          </div>
                          <h3 className="font-bold text-slate-900 text-lg mb-1">{job.title}</h3>
                          <p className="text-slate-600 mb-2">{job.company_name} • {job.location}</p>
                          <div className="flex items-center gap-4 text-sm text-slate-500 flex-wrap">
                            {(job.salary_min || job.salary_max) && (
                              <span>
                                RM {job.salary_min?.toLocaleString() || '0'} - RM {job.salary_max?.toLocaleString() || '0'}/month
                              </span>
                            )}
                            <span>Posted: {formatDate(job.created_at)}</span>
                            {job.application_deadline && (
                              <span>Deadline: {formatDate(job.application_deadline)}</span>
                            )}
                          </div>
                        </div>

                        {/* Applicants Count & Actions */}
                        <div className="flex flex-col gap-2 items-end">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => openApplicantsDialog(job)}
                            className="gap-1"
                            data-testid={`view-applicants-${job.id}`}
                          >
                            <Users className="w-4 h-4" />
                            {jobApps.length} Applicants
                          </Button>
                          <div className="flex gap-2 flex-wrap justify-end">
                            <Button variant="outline" size="sm" onClick={() => openEditDialog(job)} className="gap-1">
                              <Edit className="w-4 h-4" /> Edit
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleTogglePublish(job.id)}
                              className="gap-1"
                            >
                              {job.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              {job.is_published ? 'Unpublish' : 'Publish'}
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleDeleteJob(job.id)}
                              className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" /> Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {activeTab === 'applications' && (
        <Card>
          <CardContent className="p-0">
            {applications.length === 0 ? (
              <div className="py-12 text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h3 className="font-semibold text-slate-900 mb-2">No applications yet</h3>
                <p className="text-slate-500">Applications will appear here when workers apply for jobs</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Applicant</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Job Position</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Company</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Applied On</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {applications.map((app) => (
                      <tr key={app.id} className="hover:bg-slate-50" data-testid={`application-row-${app.id}`}>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-slate-900">{app.user_name}</p>
                            <p className="text-slate-500 text-xs">{app.user_email}</p>
                            {app.user_phone && <p className="text-slate-500 text-xs">{app.user_phone}</p>}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900">{app.job_title}</td>
                        <td className="px-4 py-3 text-slate-600">{app.company_name}</td>
                        <td className="px-4 py-3 text-slate-600">{formatDate(app.applied_at)}</td>
                        <td className="px-4 py-3">
                          <Badge className={getStatusColor(app.status)}>
                            {getStatusLabel(app.status)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Select 
                            value={app.status} 
                            onValueChange={(val) => handleUpdateApplicationStatus(app.id, val)}
                          >
                            <SelectTrigger className="w-32 h-8 text-xs" data-testid={`status-select-${app.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="submitted">Submitted</SelectItem>
                              <SelectItem value="reviewing">Reviewing</SelectItem>
                              <SelectItem value="interview">Interview</SelectItem>
                              <SelectItem value="accepted">Accepted</SelectItem>
                              <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Applicants Dialog */}
      <Dialog open={showApplicantsDialog} onOpenChange={setShowApplicantsDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Applicants for {selectedJob?.title}</DialogTitle>
            <DialogDescription>
              {selectedJob?.company_name} • {getJobApplications(selectedJob?.id || '').length} applicants
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {selectedJob && getJobApplications(selectedJob.id).length === 0 ? (
              <div className="py-8 text-center text-slate-500">
                <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p>No applicants yet for this position</p>
              </div>
            ) : (
              <div className="space-y-4">
                {selectedJob && getJobApplications(selectedJob.id).map((app) => (
                  <Card key={app.id}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start gap-4">
                        <div>
                          <h4 className="font-semibold text-slate-900">{app.user_name}</h4>
                          <p className="text-sm text-slate-500">{app.user_email}</p>
                          {app.user_phone && <p className="text-sm text-slate-500">{app.user_phone}</p>}
                          <p className="text-xs text-slate-400 mt-1">Applied: {formatDate(app.applied_at)}</p>
                          {app.cover_letter && (
                            <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                              <p className="text-xs font-medium text-slate-600 mb-1">Cover Letter:</p>
                              <p className="text-sm text-slate-700">{app.cover_letter}</p>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge className={getStatusColor(app.status)}>
                            {getStatusLabel(app.status)}
                          </Badge>
                          <Select 
                            value={app.status} 
                            onValueChange={(val) => handleUpdateApplicationStatus(app.id, val)}
                          >
                            <SelectTrigger className="w-32 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="submitted">Submitted</SelectItem>
                              <SelectItem value="reviewing">Reviewing</SelectItem>
                              <SelectItem value="interview">Interview</SelectItem>
                              <SelectItem value="accepted">Accepted</SelectItem>
                              <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplicantsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Deleted Users View
function DeletedUsersView() {
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeletedUsers();
  }, []);

  const fetchDeletedUsers = async () => {
    try {
      const response = await axios.get('/users/deleted');
      setDeletedUsers(response.data);
    } catch (error) {
      toast.error("Failed to load deleted users");
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreUser = async (userId, userName) => {
    try {
      await axios.post(`/users/${userId}/restore`);
      toast.success(`${userName} has been restored`);
      fetchDeletedUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore user");
    }
  };

  const handlePermanentDelete = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to PERMANENTLY delete ${userName}? This cannot be undone!`)) {
      return;
    }
    
    try {
      await axios.delete(`/users/${userId}/permanent`);
      toast.success(`${userName} has been permanently deleted`);
      fetchDeletedUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  if (loading) {
    return <div className="p-6">Loading deleted users...</div>;
  }

  const deletedMembers = deletedUsers.filter(u => u.role === "member");
  const deletedStaff = deletedUsers.filter(u => u.role === "staff");

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Deleted Users</h1>
        <p className="text-slate-600 text-lg">View and restore deleted members and staff.</p>
      </div>

      {/* Deleted Members Section */}
      <Card className="border-none shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Deleted Members ({deletedMembers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {deletedMembers.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p>No deleted members</p>
            </div>
          ) : (
            <div className="space-y-4">
              {deletedMembers.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg bg-red-50 border-red-100">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="bg-red-100 text-red-600 font-semibold">
                        {user.full_name.split(" ").map(n => n[0]).join("").substring(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold">{user.full_name}</p>
                      <p className="text-sm text-slate-600">{user.email}</p>
                      <p className="text-xs text-slate-400">Deleted: {new Date(user.deleted_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={() => handleRestoreUser(user.id, user.full_name)}
                      data-testid={`restore-user-${user.id}`}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Restore
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handlePermanentDelete(user.id, user.full_name)}
                      data-testid={`permanent-delete-${user.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete Forever
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deleted Staff Section */}
      <Card className="border-none shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Deleted Staff ({deletedStaff.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {deletedStaff.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p>No deleted staff</p>
            </div>
          ) : (
            <div className="space-y-4">
              {deletedStaff.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg bg-red-50 border-red-100">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="bg-red-100 text-red-600 font-semibold">
                        {user.full_name.split(" ").map(n => n[0]).join("").substring(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold">{user.full_name}</p>
                      <p className="text-sm text-slate-600">{user.email}</p>
                      <p className="text-xs text-slate-400">Deleted: {new Date(user.deleted_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={() => handleRestoreUser(user.id, user.full_name)}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Restore
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handlePermanentDelete(user.id, user.full_name)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete Forever
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}



export default function AdminDashboard({ user, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState({
    totalStaff: 0,
    totalWorkers: 0,
    totalComplaints: 0,
    resolvedComplaints: 0,
    totalRevenue: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [membersRes, complaintsRes, staffRes] = await Promise.all([
        axios.get('/members'),
        axios.get('/complaints'),
        axios.get('/staff').catch(() => ({ data: [] }))
      ]);
      
      setStats({
        totalStaff: staffRes.data.length || 0,
        totalWorkers: membersRes.data.length,
        totalComplaints: complaintsRes.data.length,
        resolvedComplaints: complaintsRes.data.filter(c => c.status === 'resolved').length,
        totalRevenue: membersRes.data.reduce((sum, m) => sum + (m.monthly_fee || 0), 0)
      });
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const memberId = location.pathname.split("/").pop();
  const isDetailView = location.pathname.includes("/admin/members/") && memberId !== "members";

  const navItems = [
    { path: "/admin", label: "Dashboard", icon: Home },
    { path: "/admin/pending-approvals", label: "Pending Approvals", icon: UserPlus, superAdminOnly: true },
    { path: "/admin/announcements", label: "Announcements", icon: Megaphone },
    { path: "/admin/jobs", label: "Jobs", icon: Briefcase },
    { path: "/admin/payments", label: "Payments", icon: CreditCard },
    { path: "/admin/visa-tracker", label: "Visa Tracker", icon: FileText },
    { path: "/admin/live-chat", label: "Chat Oversight", icon: MessageSquare },
    { path: "/admin/members", label: "Members", icon: Users },
    { path: "/admin/members", label: "Worker Membership", icon: Briefcase, hide: true },
    { path: "/admin/staff", label: "Staff Management", icon: UserPlus },
    { path: "/admin/deleted-users", label: "Deleted Users", icon: Trash2 },
    { path: "/admin/reports", label: "Reports", icon: Download },
    { path: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  ];

  const SidebarContent = ({ onNavClick }) => (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-slate-800">
        <Link to="/admin" className="flex items-center gap-3" onClick={onNavClick}>
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-bold">Admin Portal</span>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6">
        <p className="px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Navigation</p>
        <div className="space-y-1 px-3">
          {navItems.filter(item => !item.hide && (!item.superAdminOnly || user?.role === "super_admin")).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
                            (item.path === "/admin/members" && location.pathname.includes("/admin/members"));
            return (
              <Link
                key={item.path + item.label}
                to={item.path}
                onClick={onNavClick}
                className={`flex items-center gap-3 px-3 py-3 rounded-lg transition ${
                  isActive
                    ? "bg-blue-600 text-white"
                    : "text-slate-400 hover:bg-slate-800 hover:text-white"
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium text-sm">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Sign Out */}
      <div className="p-3 border-t border-slate-800">
        <button
          onClick={() => { onNavClick?.(); onLogout(); }}
          className="flex items-center gap-3 px-3 py-3 rounded-lg w-full text-red-400 hover:bg-slate-800 transition"
          data-testid="sidebar-logout"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium text-sm">Sign Out</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex-col hidden md:flex" data-testid="admin-sidebar">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-slate-900 text-white flex flex-col shadow-xl animate-slide-in">
            <SidebarContent onNavClick={() => setMobileMenuOpen(false)} />
          </aside>
        </div>
      )}

      {/* Mobile Menu Animation Styles */}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-200 px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="admin-mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            
            {/* Mobile Logo */}
            <Link to="/admin" className="md:hidden flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-slate-900">Admin</span>
            </Link>

            <div className="flex items-center gap-2 md:gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold text-slate-900">{user.email}</p>
                <Badge variant="secondary" className="text-xs mt-1">Super Admin</Badge>
              </div>
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-blue-100 text-blue-600 font-semibold">
                  {user.full_name?.charAt(0) || 'A'}
                </AvatarFallback>
              </Avatar>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-8 overflow-auto">
          <div>
            <Routes>
              <Route path="/" element={<DashboardHomeView stats={stats} />} />
              <Route path="/pending-approvals" element={<PendingApprovalsView user={user} />} />
              <Route path="/announcements" element={<AnnouncementsManagementView />} />
              <Route path="/jobs" element={<JobsManagementView />} />
              <Route path="/visa-tracker" element={<VisaTrackerView />} />
              <Route path="/live-chat" element={<LiveChatView />} />
              <Route path="/members" element={<MembersView user={user} />} />
              <Route path="/members/:id" element={<MemberDetailView memberId={memberId} />} />
              <Route path="/staff" element={<StaffManagementView stats={stats} />} />
              <Route path="/deleted-users" element={<DeletedUsersView />} />
              <Route path="/workload" element={<WorkloadOverviewView />} />
              <Route path="/reports" element={<ReportsView />} />
              <Route path="/analytics" element={<AnalyticsView stats={stats} />} />
              <Route path="/complaints" element={<ComplaintsView />} />
              <Route path="/payments" element={<PaymentsView user={user} />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}